import { memo, useState, type FormEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@base-ui/react/popover';
import type { NamedLayout } from '@apis/preferences';
import type { CardId, DashboardLayoutItem } from '@interfaces/IDashboardLayout';
import { Button } from '@components/ui/Button/Button';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { ChevronDownIcon, CheckIcon, EyeIcon, EyeOffIcon, PencilIcon, TrashIcon } from '@utils/icons';
import { CARD_IDS, CARD_TITLE_KEYS, DEFAULT_LAYOUT_ID } from './layout.defaults';
import styles from './dashboardLayoutControls.module.css';

interface IDashboardLayoutControlsProps {
  layouts: NamedLayout[];
  activeLayoutId: string;
  editing: boolean;
  canUpdateActive: boolean;
  /** Current layout items; drives the Cards menu's shown/hidden state. Only read
   *  while editing. Named `layoutItems`, not `layout`, because this file already
   *  uses `layout` for a single `NamedLayout` in the switcher below. */
  layoutItems: DashboardLayoutItem[];
  onSelect: (id: string) => void;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  /** Toggle one card's visibility in the edit draft. */
  onToggleCard: (id: CardId) => void;
}

export const DashboardLayoutControls = memo(function DashboardLayoutControls({
  layouts,
  activeLayoutId,
  editing,
  canUpdateActive,
  layoutItems,
  onSelect,
  onStartEdit,
  onCancel,
  onSave,
  onRename,
  onDelete,
  onToggleCard,
}: IDashboardLayoutControlsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const activeName =
    activeLayoutId === DEFAULT_LAYOUT_ID
      ? t('dashboard.layout.default')
      : layouts.find((layout) => layout.id === activeLayoutId)?.name ?? t('dashboard.layout.default');

  const select = (id: string) => {
    if (id !== activeLayoutId) onSelect(id);
    setOpen(false);
  };

  // Open/close of the save popover. On open, pre-fill the name with the active
  // saved (non-default) layout's name so the default action overwrites it;
  // editing the read-only Default starts blank.
  const onSaveOpenChange = (next: boolean) => {
    setSaveOpen(next);
    if (next) {
      setNewName(canUpdateActive && activeLayoutId !== DEFAULT_LAYOUT_ID ? activeName : '');
    }
  };

  const trimmed = newName.trim();
  // Whether the typed name will overwrite an existing saved layout — drives the
  // confirm-button label (update vs. create). The hook owns the actual routing.
  const matchedLayout = layouts.find((layout) => layout.name === trimmed);

  const submitSave = (event: FormEvent) => {
    event.preventDefault();
    if (!trimmed) return;
    onSave(trimmed);
    setNewName('');
    setSaveOpen(false);
  };

  const startRename = (event: MouseEvent, layout: NamedLayout) => {
    event.stopPropagation();
    setRenamingId(layout.id);
    setRenameValue(layout.name);
  };
  const commitRename = (event: FormEvent) => {
    event.preventDefault();
    const name = renameValue.trim();
    if (renamingId && name) onRename(renamingId, name);
    setRenamingId(null);
  };
  const remove = (event: MouseEvent, id: string) => {
    event.stopPropagation();
    onDelete(id);
  };

  // EDITING: [Cancel] [Cards n/total ▾] [Save▾]. The Save button is a popover
  // trigger whose body holds the name input; submit routes to update-or-create
  // via onSave. The Cards trigger opens the per-card visibility list.
  if (editing) {
    // Hidden ids come from the draft layout; the rows themselves iterate
    // CARD_IDS instead, because the layout array's order is arbitrary (saved
    // layouts append merged-in cards at the end) and would make rows jump.
    // A card absent from the layout has no `hidden` flag, so it reads as shown.
    const hiddenIds = new Set(layoutItems.filter((item) => item.hidden).map((item) => item.i));
    const shownCount = CARD_IDS.length - hiddenIds.size;
    const counts = { shown: shownCount, total: CARD_IDS.length };

    return (
      <div className={styles.controls}>
        <Button variant="ghost" size="md" onClick={onCancel}>
          {t('dashboard.layout.cancel')}
        </Button>

        {/* Reuses `.trigger` (the neutral 40px select-style button) so the
            Cards control sits between ghost Cancel and primary Save without a
            third visual weight. No Popover.Close inside: toggles must not
            dismiss the popup, since users flip several in one pass. */}
        <Popover.Root open={cardsOpen} onOpenChange={setCardsOpen}>
          <Popover.Trigger className={styles.trigger}>
            <span className={styles.triggerLabel}>{t('dashboard.layout.cards')}</span>
            <span className={styles.cardsCount}>{t('dashboard.layout.cardsCount', counts)}</span>
            <ChevronDownIcon size={14} />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Positioner side="bottom" align="end" sideOffset={6} className={styles.positioner}>
              <Popover.Popup className={`${styles.popup} ${styles.cardsPopup}`}>
                <p className={styles.popupTitle}>{t('dashboard.layout.cardsTitle', counts)}</p>

                <ScrollArea className={styles.cardScroll}>
                  <ul className={styles.cardList}>
                    {CARD_IDS.map((id) => {
                      const shown = !hiddenIds.has(id);
                      const name = t(CARD_TITLE_KEYS[id]);
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={shown}
                            // No aria-label: the visible card name IS the accessible
                            // name, so the two can't drift apart (and voice control
                            // keeps working). role + aria-checked carry the state; the
                            // title spells out what a click will do.
                            title={t(shown ? 'dashboard.layout.hideCard' : 'dashboard.layout.showCard', { name })}
                            className={`${styles.cardRow} ${shown ? '' : styles.cardRowHidden}`}
                            onClick={() => onToggleCard(id)}
                          >
                            <span className={styles.eyeSlot}>
                              {shown ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}
                            </span>
                            <span className={styles.cardName}>{name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </ScrollArea>

                {/* Only the built-in Default is read-only; saved layouts update in place. */}
                {activeLayoutId === DEFAULT_LAYOUT_ID && (
                  <p className={styles.cardsFoot}>{t('dashboard.layout.defaultReadOnly')}</p>
                )}
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>

        <Popover.Root open={saveOpen} onOpenChange={onSaveOpenChange}>
          <Popover.Trigger className={styles.saveTrigger}>
            {t('dashboard.layout.save')}
            <ChevronDownIcon size={14} />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Positioner side="bottom" align="end" sideOffset={6} className={styles.positioner}>
              <Popover.Popup className={styles.savePopup}>
                <form className={styles.saveForm} onSubmit={submitSave}>
                  <input
                    className={styles.saveInput}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t('dashboard.layout.namePrompt')}
                    maxLength={60}
                    autoFocus
                  />
                  <Button type="submit" variant="primary" size="md" block disabled={!trimmed}>
                    {matchedLayout
                      ? t('dashboard.layout.update', { name: matchedLayout.name })
                      : t('dashboard.layout.save')}
                  </Button>
                </form>
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
      </div>
    );
  }

  // NOT EDITING: selector popover (switch / rename / delete) + Edit layout.
  return (
    <div className={styles.controls}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger className={styles.trigger}>
          <span className={styles.triggerLabel}>{activeName}</span>
          <ChevronDownIcon size={14} />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner side="bottom" align="end" sideOffset={6} className={styles.positioner}>
            <Popover.Popup className={styles.popup}>
              <p className={styles.popupTitle}>{t('dashboard.layout.title')}</p>

              <ScrollArea className={styles.listScroll}>
                <ul className={styles.list}>
                  <li>
                    <button
                      type="button"
                      className={`${styles.row} ${activeLayoutId === DEFAULT_LAYOUT_ID ? styles.rowActive : ''}`}
                      onClick={() => select(DEFAULT_LAYOUT_ID)}
                    >
                      <span className={styles.check}>
                        {activeLayoutId === DEFAULT_LAYOUT_ID && <CheckIcon size={14} />}
                      </span>
                      <span className={styles.rowName}>{t('dashboard.layout.default')}</span>
                    </button>
                  </li>
  
                  {layouts.map((layout) => (
                    <li key={layout.id}>
                      {renamingId === layout.id ? (
                        <form className={styles.renameForm} onSubmit={commitRename}>
                          <input
                            className={styles.renameInput}
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={commitRename}
                            autoFocus
                            maxLength={60}
                          />
                        </form>
                      ) : (
                        <div className={`${styles.row} ${activeLayoutId === layout.id ? styles.rowActive : ''}`}>
                          <button type="button" className={styles.rowSelect} onClick={() => select(layout.id)}>
                            <span className={styles.check}>
                              {activeLayoutId === layout.id && <CheckIcon size={14} />}
                            </span>
                            <span className={styles.rowName}>{layout.name}</span>
                          </button>
                          <span className={styles.rowActions}>
                            <button
                              type="button"
                              className={styles.iconBtn}
                              aria-label={t('dashboard.layout.rename')}
                              onClick={(e) => startRename(e, layout)}
                            >
                              <PencilIcon size={14} />
                            </button>
                            <button
                              type="button"
                              className={styles.iconBtn}
                              aria-label={t('dashboard.layout.delete')}
                              onClick={(e) => remove(e, layout.id)}
                            >
                              <TrashIcon size={14} />
                            </button>
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>

      <Button variant="primary" size="md" onClick={onStartEdit}>
        {t('dashboard.layout.edit')}
      </Button>
    </div>
  );
});
